























































































































































import {
  computed,
  defineComponent,
  nextTick,
  ref,
  useRoute,
  PropType,
  toRefs,
  watch
} from '@nuxtjs/composition-api'
import CSlider from '~/components/shared/configurable/slider/CSlider.vue'
import { useNamespacedStore } from '~/compositions/store'
import {
  CLASSIFIED_VIEW_NS,
  ClassifiedViewState
} from '~/store/modules/shared/classifieds/view/state'
import { formatNumber } from '~/utils/number'
import { useDeps } from '~/compositions/dependency-container'
import LoanService from '~/services/loan/LoanService'
import { LoanMerchantName } from '~/models/loan'
import { useLogger } from '~/compositions/logger'
import CShimmer from '~/components/shared/configurable/shimmer/CShimmer.vue'
import LoanCalculatorModal from '~/components/car/classifieds/view/partials/loans/calculator/LoanCalculatorModal.vue'
import {
  TBI_MAX_AUTO_LOAN_AMOUNT,
  TBI_MAX_GENERIC_LOAN_AMOUNT,
  TBI_MIN_AUTO_LOAN_AMOUNT,
  TBI_MIN_GENERIC_LOAN_AMOUNT,
  TbiLoanType
} from '~/constants/loan/tbi'
import { debounce } from '~/utils/function'
import { DEBOUNCE_SWIFT } from '~/constants/debounce'
import LoanCalculatorTimeButton from '~/components/car/classifieds/view/partials/loans/calculator/LoanCalculatorTimeButton.vue'
import { arraysEqual } from '~/utils/array'
import { v4 as uuidv4 } from 'uuid'

export default defineComponent({
  props: {
    sessionId: {
      type: String,
      default: () => uuidv4()
    },
    loanType: {
      type: String as PropType<TbiLoanType>,
      required: false,
      default: TbiLoanType.AUTO
    }
  },
  components: {
    LoanCalculatorTimeButton,
    CSlider,
    CShimmer,
    LoanCalculatorModal
  },
  setup(props, { emit }) {
    const { loanType } = toRefs(props)
    const { state } = useNamespacedStore<ClassifiedViewState>(
      CLASSIFIED_VIEW_NS
    )
    const [loanService] = useDeps(LoanService)
    const logger = useLogger()
    const route = useRoute()

    const classifiedRawPrice = computed(
      () => state.classified?.price.extra.raw_price || 0
    )

    const sliderRef = ref<typeof CSlider | null>(null)

    // Maybe change these values later, when the business logic is finalized
    // TODO: handle <1000 vehicles
    const minLoanAmount = computed<number>(() =>
      loanType.value === TbiLoanType.CONSUMER
        ? TBI_MIN_GENERIC_LOAN_AMOUNT
        : TBI_MIN_AUTO_LOAN_AMOUNT
    )
    const maxLoanAmount = computed<number>(() => {
      if (
        ['__landing_pages_tbi_blackfriday', '__landing_pages_tbi'].includes(
          route.value.name as string
        )
      )
        return 15000

      const maxToUse =
        loanType.value === TbiLoanType.CONSUMER
          ? TBI_MAX_GENERIC_LOAN_AMOUNT
          : TBI_MAX_AUTO_LOAN_AMOUNT
      if (loanType.value === TbiLoanType.CONSUMER) {
        return maxToUse
      } else if (classifiedRawPrice.value > maxToUse) {
        return maxToUse
      } else if (classifiedRawPrice.value < minLoanAmount.value) {
        return minLoanAmount.value
      }
      return Math.trunc(classifiedRawPrice.value)
    })

    const initLoanAmount = computed(() => {
      if (loanType.value === TbiLoanType.CONSUMER) {
        if (classifiedRawPrice.value > TBI_MAX_GENERIC_LOAN_AMOUNT) {
          return TBI_MAX_GENERIC_LOAN_AMOUNT
        } else if (classifiedRawPrice.value < minLoanAmount.value) {
          return minLoanAmount.value
        }
        return classifiedRawPrice.value
      }
      return maxLoanAmount.value
    })
    const loanAmount = ref(initLoanAmount.value)
    const liveLoanAmount = ref(initLoanAmount.value)

    const monthModeTrigger = computed(() => {
      return (
        loanType.value === TbiLoanType.CONSUMER && liveLoanAmount.value <= 500
      )
    })

    const monthMode = ref<boolean>(monthModeTrigger.value)

    const loanInstallments = computed(() =>
      monthModeTrigger.value ? selectedTime.value : selectedTime.value * 12
    )

    const selectedTime = ref(monthModeTrigger.value ? 12 : 4) // 12 months : 4 years

    const loanInstallment = ref(
      loanService.calculateInstallmentAmount(
        loanAmount.value,
        loanInstallments.value
      )
    )
    const loadingLoanInfo = ref(false)
    const loanModalIsShown = ref(false)

    const inputRef = ref(null)
    const showInput = ref(false)

    function timeButtonIsSelected(time: number): boolean {
      return selectedTime.value === time
    }

    function handleTimeButtonClick(time: number) {
      if (selectedTime.value !== time) {
        selectedTime.value = time
        loadLoanInfo()
      }
    }

    const sliderTooltipFormatter = (v: number) => `${formatNumber(v)} €`

    async function loadLoanInfo() {
      if (loadingLoanInfo.value) {
        // still loading previous request
        return
      }

      loadingLoanInfo.value = true
      try {
        const { installment } = await loanService.getLoanInfo(
          LoanMerchantName.TBI,
          loanAmount.value,
          loanInstallments.value,
          loanType.value
        )
        loanInstallment.value = installment
      } catch (error) {
        logger.captureError(error as any)
      } finally {
        loadingLoanInfo.value = false

        emit('update-loan-amount', loanAmount.value)
        emit('update-loan-installments', loanInstallments.value)
      }
    }

    const handleAmountChange = debounce(loadLoanInfo, DEBOUNCE_SWIFT)

    function handleGetALoanButtonClick() {
      loanModalIsShown.value = true
    }

    const formattedLoanInstallment = computed(() =>
      loanInstallment.value.toFixed(2)
    )

    const inputWidth = computed(() => {
      const letterWidth = 11 // average
      if (loanAmount.value) {
        return `${formatNumber(loanAmount.value).toString().length *
          letterWidth}px`
      }
      return `${letterWidth}px`
    })

    const onBlur = () => {
      showInput.value = false

      if (loanAmount.value <= minLoanAmount.value) {
        loanAmount.value = minLoanAmount.value
      } else if (loanAmount.value >= maxLoanAmount.value) {
        loanAmount.value = maxLoanAmount.value
      }

      handleAmountChange()
    }

    const onLabelClick = async () => {
      showInput.value = true
      await nextTick()
      inputRef.value.focus()
    }

    const onDragging = (_v: number) => {
      liveLoanAmount.value = _v
    }

    watch(loanAmount, val => {
      liveLoanAmount.value = val
    })

    const yearsArray = [1, 2, 3, 4, 5]
    const monthsArray = [3, 6, 12, 24]
    const timeOptions = ref<number[]>(
      monthModeTrigger.value ? monthsArray : yearsArray
    )
    const nextTimeOptions = ref<number[]>(
      monthModeTrigger.value ? yearsArray : monthsArray
    )

    const enterAnimationAtLine = ref<number>(timeOptions.value.length - 1)
    const leaveAnimationAtLine = ref<number>(-1)

    const triggerAnimation = async (showMonths: boolean) => {
      const previousTimeOptions = [...timeOptions.value]
      const nextTimeOptionsToSet = showMonths ? monthsArray : yearsArray
      if (arraysEqual(nextTimeOptionsToSet, previousTimeOptions)) {
        // we are trying to set the exact same times
        enterAnimationAtLine.value = nextTimeOptionsToSet.length - 1
        return
      }
      nextTimeOptions.value = nextTimeOptionsToSet

      if (enterAnimationAtLine.value === previousTimeOptions.length - 1) {
        leaveAnimationAtLine.value = enterAnimationAtLine.value
      }

      enterAnimationAtLine.value = -1
      await nextTick()
    }

    watch(monthModeTrigger, async val => {
      await triggerAnimation(val)
    })

    const afterLineEnter = () => {
      if (enterAnimationAtLine.value < timeOptions.value.length - 1) {
        enterAnimationAtLine.value++
      }
    }

    const afterLineLeave = async () => {
      if (leaveAnimationAtLine.value >= 0) {
        leaveAnimationAtLine.value--
      }
      if (leaveAnimationAtLine.value === -1) {
        timeOptions.value = nextTimeOptions.value

        if (monthModeTrigger.value && !monthMode.value) {
          // we are going to month mode
          const selectedTimeInMonths = selectedTime.value * 12
          selectedTime.value =
            timeOptions.value.find(t => t === selectedTimeInMonths) ||
            monthsArray[2]
        } else if (!monthModeTrigger.value && monthMode.value) {
          // we are going to year mode
          const selectedTimeInYears = selectedTime.value / 12
          selectedTime.value =
            timeOptions.value.find(t => t === selectedTimeInYears) ||
            yearsArray[3]
        }

        monthMode.value = monthModeTrigger.value

        await nextTick()
        enterAnimationAtLine.value = 0
      }
    }

    const sliderMarks = computed(() => {
      const marks = {
        [minLoanAmount.value.toString()]: {
          label: ``
        },
        [maxLoanAmount.value.toString()]: {
          label: ``
        }
      }
      if (
        loanType.value === TbiLoanType.CONSUMER &&
        maxLoanAmount.value > 500
      ) {
        // add the dummy mark at 500 so as to show the difference in colors between the two loan categories
        const markPoint = 500
        const percentage =
          ((markPoint - minLoanAmount.value) /
            (maxLoanAmount.value - minLoanAmount.value)) *
          100

        const containerWidth =
          process.client && sliderRef.value
            ? sliderRef.value.$el.clientWidth
            : 4

        const widthValue = `${(containerWidth * percentage) / 100 - 3}px`

        marks[markPoint.toString()] = {
          label: '',
          style: {
            width: widthValue,
            height: '4px',
            borderRadius: '0px',
            display: 'block',
            backgroundColor: 'var(--color-grey-500)',
            transform: 'translate(-100%, 0px)',
            opacity: 0.5
          }
        }

        // add mark for the classified price
        if (initLoanAmount.value >= 1800 && initLoanAmount.value <= 8000) {
          // > 1800 becuase otherwise it will overlap with the other marks
          marks[initLoanAmount.value.toString()] = {
            label: sliderTooltipFormatter(initLoanAmount.value.toString()),
            labelStyle: {
              color: 'var(--color-grey-500)',
              fontSize: '0.75rem',
              marginTop: '0.8rem',
              cursor: 'pointer'
            }
          }
        }
      }

      return marks
    })

    const onDragEnd = async () => {
      await nextTick()
      triggerAnimation(monthModeTrigger.value)
    }

    return {
      selectedTime,
      timeButtonIsSelected,
      handleTimeButtonClick,
      sliderTooltipFormatter,
      loanAmount,
      classifiedRawPrice,
      maxLoanAmount,
      minLoanAmount,
      formatNumber,
      handleAmountChange,
      loanInstallment,
      loadingLoanInfo,
      handleGetALoanButtonClick,
      loanModalIsShown,
      formattedLoanInstallment,
      loanInstallments,
      inputWidth,
      showInput,
      inputRef,
      onBlur,
      onLabelClick,
      onDragging,
      liveLoanAmount,
      monthModeTrigger,
      monthMode,
      timeOptions,
      nextTimeOptions,
      yearsArray,
      monthsArray,
      enterAnimationAtLine,
      leaveAnimationAtLine,
      afterLineEnter,
      afterLineLeave,
      sliderMarks,
      sliderRef,
      triggerAnimation,
      onDragEnd,
      TbiLoanType
    }
  }
})
